import { useEffect, useState } from 'react';
import { Alert, Button, Code, CopyButton, Group, Loader, Stack, Text } from '@mantine/core';
import { AlertTriangle, Copy } from 'lucide-react';
import type { ProviderConfigField } from '@bmai/shared';

interface Props {
    providerTypeId: string;
    field: ProviderConfigField;
    /** Current value from the form (pre-populated when editing an existing connection). */
    value: string | undefined;
    /** Fetches the server-minted secret. Idempotent — backend mints once per (tenant, provider, field). */
    fetchSecret: (providerTypeId: string, route: string) => Promise<{ value: string }>;
    /** Writes the fetched value into the wizard's form state. */
    onValueChange: (value: string) => void;
}

/**
 * Read-only Code + Copy display for fields declared with `tenantSecret`.
 * On mount, fetches the server-minted value (unless the form already has one
 * — edit-mode case where the value persisted on the connection's config).
 * Writes the value into form state so it flows into save() like any other field.
 */
export default function ConnectionTenantSecret({
    providerTypeId,
    field,
    value,
    fetchSecret,
    onValueChange,
}: Props): React.ReactElement {
    const [loading, setLoading] = useState(value === undefined || value === '');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (value && value !== '') {
            setLoading(false);
            return;
        }
        if (!field.tenantSecret) {
            setLoading(false);
            setError('Field has no tenantSecret route declared.');
            return;
        }
        const route = field.tenantSecret.route;
        let cancelled = false;
        setLoading(true);
        fetchSecret(providerTypeId, route)
            .then(({ value: minted }) => {
                if (cancelled) return;
                onValueChange(minted);
                setLoading(false);
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                setError(e instanceof Error ? e.message : String(e));
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [providerTypeId, field, fetchSecret, onValueChange, value]);

    return (
        <Stack gap={4}>
            <Text size="sm" fw={500}>
                {field.displayLabel}
                {field.required ? ' *' : ''}
            </Text>
            {field.description && (
                <Text size="xs" c="dimmed" mb={4}>
                    {field.description}
                </Text>
            )}
            {error ? (
                <Alert variant="light" color="red" icon={<AlertTriangle size={14} />}>
                    <Text size="xs">Couldn't load {field.displayLabel}: {error}</Text>
                </Alert>
            ) : loading ? (
                <Group gap="xs">
                    <Loader size="xs" />
                    <Text size="xs" c="dimmed">Generating…</Text>
                </Group>
            ) : (
                <Group gap={6} align="center" wrap="nowrap">
                    <Code style={{ flex: 1, overflowWrap: 'anywhere' }}>{value}</Code>
                    <CopyButton value={value ?? ''}>
                        {({ copied, copy }) => (
                            <Button
                                variant="default"
                                size="xs"
                                leftSection={<Copy size={12} />}
                                onClick={copy}
                            >
                                {copied ? 'Copied' : 'Copy'}
                            </Button>
                        )}
                    </CopyButton>
                </Group>
            )}
        </Stack>
    );
}
